import { Box, Button, Center, Text } from '@chakra-ui/react';
import { TEXT_CREATED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { addNewWidget } from 'modules/Editor/store';
import { useAppDispatch } from 'modules/Editor/store/hooks';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';
import { DefaultTextType, TextTagStyleConfig } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';

interface TextButtonProps {
  type: DefaultTextType;
  style: TextTagStyleConfig;
  customFontFamily: string | undefined;
  text?: string;
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

const TEXT_BUTTON_WIDTH = 320;
const TEXT_BUTTON_HEIGHT = 58;

type TextIconProps = {
  fontFamily: string;
  isBold: boolean;
  isItalic: boolean;
  color: string;
  fontSize: number;
  text?: string;
  type: DefaultTextType;
};
const TextIcon = ({ fontFamily, isBold, isItalic, color, fontSize, text, type }: TextIconProps) => (
  <Text
    as='span'
    p={3}
    fontFamily={fontFamily}
    fontWeight={isBold ? 'bold' : 'normal'}
    fontStyle={isItalic ? 'italic' : 'normal'}
    color={color}
    fontSize={`${fontSize}px`}
    textTransform={'capitalize'}
  >
    {text || type}
  </Text>
);

export const TextButton = ({ type, style, customFontFamily, text, buttonRef }: TextButtonProps) => {
  const { isBold, isItalic, fontSize, color, fontFamily: defaultFontFamily } = style;
  const fontFamily = customFontFamily || defaultFontFamily;

  const dispatch = useAppDispatch();

  const createWidgetData = () => {
    const { top: topPx, left: leftPx } = getInitialWidgetPosition();
    const widgetData = generateDefaultData(type, undefined, undefined, { topPx, leftPx }, { fontFamily });
    return widgetData;
  };

  const doAddTextWidget = () => {
    const textWidget = createWidgetData();

    dispatch(addNewWidget(textWidget));
    Mixpanel.track(TEXT_CREATED.eventName, {
      from: TEXT_CREATED.from.LeftPanel,
      text_style: type.replace(type[0], type[0].toUpperCase()),
    });
  };

  return (
    <WidgetDragDropWrapper
      widgetData={createWidgetData()}
      type={WidgetType.Text}
      dragImage={
        <Box width={`${TEXT_BUTTON_WIDTH}px`} height={`${TEXT_BUTTON_HEIGHT}px`}>
          <Center>
            <TextIcon
              type={type}
              isBold={isBold}
              isItalic={isItalic}
              color={color}
              fontSize={fontSize}
              fontFamily={fontFamily}
              text={text}
            />
          </Center>
        </Box>
      }
      dragImageOffset={{ x: TEXT_BUTTON_WIDTH / 2, y: TEXT_BUTTON_HEIGHT / 2 }}
      trackingEventData={{
        event_name: TEXT_CREATED.eventName,
        properties: {
          from: TEXT_CREATED.from.LeftPanel,
          text_style: type.replace(type[0], type[0].toUpperCase()),
        },
      }}
    >
      <Button
        ref={buttonRef}
        variant='ghost'
        onClick={doAddTextWidget}
        _hover={{ bg: 'hover.gray' }}
        justifyContent='flex-start'
        px={2}
        py={7}
        border='1px solid'
        borderColor='divider.gray'
        overflow='hidden'
        data-tag-type={type}
        width='100%'
      >
        <TextIcon
          type={type}
          isBold={isBold}
          isItalic={isItalic}
          color={color}
          fontSize={fontSize}
          fontFamily={fontFamily}
          text={text}
        />
      </Button>
    </WidgetDragDropWrapper>
  );
};
